import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  InputBase,
  Popover,
  Typography
} from '@mui/material';
import { unwrapResult } from '@reduxjs/toolkit';
import { Form } from 'antd';
import { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Snackbars from '../../../../../GlobalComponents/Alerts/Snackbars';
import GridContainer from '../../../../../GlobalComponents/Grid/GridContainer';
import GridItem from '../../../../../GlobalComponents/Grid/GridItem';
import H4 from '../../../../../GlobalComponents/TypoGraphy/H4';
import { Colors } from '../../../../../GlobalComponents/Utility/Colors';
import { addAssignmentHistory } from '../../../../../ReduxTLK/features/SAV/TicketSlice';
import { OBJECT_TYPES } from '../../../../../utils/reassignmentTypes';


/* ── Deterministic avatar color from a string (agent id/name) ── */
const AVATAR_COLORS = ['#2A66C6', '#3C3489', '#0E8A6A', '#C2410C', '#9333EA', '#0891B2', '#BE185D', '#4D7C0F'];
const colorFor = (key = '') => {
  let h = 0;
  for (let i = 0; i < String(key).length; i++) h = String(key).charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

/* Agent shape: { id, nom, prenom } */
const agentLabel = (a) =>
  a.prenom ? `${a.prenom} ${a.nom ?? ''}`.trim() : (a.nom ?? '—');
const agentInitials = (a) => {
  const first = a.prenom?.trim()?.[0] ?? '';
  const last  = a.nom?.trim()?.[0] ?? '';
  return ((first + last).toUpperCase()) || '?';
};

/* Accent-insensitive, case-insensitive normalize for search */
const normalize = (s = '') =>
  s.toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/* Small inline swap glyph for the header badge */
const SwapGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M7 7h11l-3-3M17 17H6l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);


// ─── Modal : Réaffecter le ticket (POST AddAssignmentHistory)
function ReassignModal({ open, onClose, ticketId, onSuccess }) {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const agentsSAV = useSelector(s => s.user.agentsSAV);
  console.log("Agents SAV for reassignment:", agentsSAV);

  const [userId, setUserId]         = useState(null);
  const [objectType, setObjectType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg]               = useState('');
  const [success, setSuccess]       = useState(false);
  const [error, setError]           = useState(false);

  /* Agent picker (searchable popover) */
  const anchorRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery]           = useState('');

  const selectedAgent = agentsSAV?.find(a => a.id === userId) ?? null;
  const isValid = userId !== null && objectType !== null;

  const filteredAgents = useMemo(() => {
    const list = agentsSAV ?? [];
    const q = normalize(query.trim());
    if (!q) return list;
    return list.filter(a => normalize(agentLabel(a)).includes(q));
  }, [agentsSAV, query]);

  const openPicker  = () => { setPickerOpen(true); };
  const closePicker = () => { setPickerOpen(false); setQuery(''); };

  const pickAgent = (agent) => {
    setUserId(agent.id);
    form.setFieldsValue({ userId: agent.id });
    closePicker();
  };

  const handleClose = () => {
    form.resetFields();
    setUserId(null);
    setObjectType(null);
    closePicker();
    onClose();
  };

  const handleFinish = async () => {
    const body = { ticketId, userId, objectType };
    setSubmitting(true);
    try {
      await dispatch(addAssignmentHistory(body)).then(unwrapResult);
      setMsg('Ticket réaffecté'); setSuccess(true);
      handleClose(); onSuccess();
    } catch { setMsg("Un problème est survenu."); setError(true); }
    finally { setSubmitting(false); }
  };

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="sm"
        sx={{ '& .MuiPaper-root': { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <GridContainer>
            <GridItem sx={11.5} sm={11.5} md={11.5} xl={11.5} xxl={11.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '12px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: 'rgba(42,102,198,0.10)', color: '#2A66C6'
                }}>
                  <SwapGlyph />
                </Box>
                <Box>
                  <H4 title="Réaffecter le ticket" />
                  <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.25 }}>
                    Choisissez un agent et le type d'objet concerné
                  </Typography>
                </Box>
              </Box>
            </GridItem>
            <GridItem sx={0.5} sm={0.5} md={0.5} xl={0.5} xxl={0.5}>
              <IconButton onClick={handleClose} sx={{ color: Colors.primary }}><CloseIcon /></IconButton>
            </GridItem>
          </GridContainer>
        </DialogTitle>

        <Form form={form} name="reassign" onFinish={handleFinish}>
          <GridContainer display="flex" justifyContent="center" direction="row" gap="8px" flex="1 0 0">
            <GridItem sx={12} sm={12} md={12} xl={12} xxl={12} padding="8px 32px 0px 32px">

              {/* ── Agent (searchable picker) ── */}
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', mb: 0.75 }}>
                Agent <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <Form.Item name="userId" rules={[{ required: true, message: 'Ce champ est obligatoire !' }]}>
                {/* Trigger */}
                <Box
                  ref={anchorRef}
                  onClick={openPicker}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    minHeight: 42, px: 1.5, mb: 2,
                    borderRadius: '10px', cursor: 'pointer', userSelect: 'none',
                    border: '1px solid',
                    borderColor: pickerOpen ? '#2A66C6' : '#E5E7EB',
                    boxShadow: pickerOpen ? '0 0 0 3px rgba(42,102,198,0.12)' : 'none',
                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                    '&:hover': { borderColor: '#2A66C6' }
                  }}
                >
                  {selectedAgent ? (
                    <>
                      <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: colorFor(selectedAgent.id) }}>
                        {agentInitials(selectedAgent)}
                      </Avatar>
                      <Typography sx={{ flex: 1, fontSize: 14, color: '#111827' }}>
                        {agentLabel(selectedAgent)}
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ flex: 1, fontSize: 14, color: '#9CA3AF' }}>
                      Sélectionner un agent
                    </Typography>
                  )}
                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: 20, color: '#9CA3AF',
                      transform: pickerOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.18s ease'
                    }}
                  />
                </Box>

                <Popover
                  open={pickerOpen}
                  anchorEl={anchorRef.current}
                  onClose={closePicker}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 0.75, width: anchorRef.current?.offsetWidth ?? 360,
                        borderRadius: '12px', overflow: 'hidden',
                        boxShadow: '0 10px 30px rgba(16,24,40,0.12), 0 0 0 1px rgba(16,24,40,0.04)'
                      }
                    }
                  }}
                >
                  {/* Search field */}
                  <Box sx={{ p: 1.25, borderBottom: '1px solid #F1F3F6' }}>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 1.25, height: 38, borderRadius: '9px',
                      backgroundColor: '#F6F8FB',
                      border: '1px solid #EDF0F4',
                      '&:focus-within': { borderColor: '#2A66C6', backgroundColor: '#fff' }
                    }}>
                      <SearchIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                      <InputBase
                        autoFocus
                        fullWidth
                        placeholder="Rechercher un agent…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        sx={{ fontSize: 13.5 }}
                      />
                      {query && (
                        <IconButton size="small" onClick={() => setQuery('')} sx={{ p: 0.25 }}>
                          <CloseIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {/* Result count */}
                  <Box sx={{ px: 1.75, pt: 1, pb: 0.5 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.3, color: '#9CA3AF', textTransform: 'uppercase' }}>
                      {filteredAgents.length} agent{filteredAgents.length > 1 ? 's' : ''}
                    </Typography>
                  </Box>

                  {/* List */}
                  <Box sx={{ maxHeight: 260, overflowY: 'auto', px: 0.75, pb: 1 }}>
                    {filteredAgents.length === 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4, color: '#9CA3AF' }}>
                        <PersonOffOutlinedIcon sx={{ fontSize: 28 }} />
                        <Typography sx={{ fontSize: 13 }}>Aucun agent trouvé</Typography>
                      </Box>
                    ) : (
                      filteredAgents.map((agent) => {
                        const active = agent.id === userId;
                        return (
                          <Box
                            key={agent.id}
                            onClick={() => pickAgent(agent)}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1.25,
                              px: 1.25, py: 0.9, my: 0.25, borderRadius: '9px',
                              cursor: 'pointer',
                              backgroundColor: active ? 'rgba(42,102,198,0.08)' : 'transparent',
                              transition: 'background-color 0.12s ease',
                              '&:hover': { backgroundColor: active ? 'rgba(42,102,198,0.10)' : '#F6F8FB' }
                            }}
                          >
                            <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: colorFor(agent.id) }}>
                              {agentInitials(agent)}
                            </Avatar>
                            <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: active ? 600 : 400, color: active ? '#2A66C6' : '#374151' }}>
                              {agentLabel(agent)}
                            </Typography>
                            {active && <CheckCircleIcon sx={{ fontSize: 18, color: '#2A66C6' }} />}
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Popover>
              </Form.Item>

              {/* ── Type d'objet ── */}
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', mb: 1 }}>
                Type d'objet <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <Form.Item name="objectType" rules={[{ required: true, message: 'Ce champ est obligatoire !' }]}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1 }}>
                  {OBJECT_TYPES.map((item) => {
                    const active = objectType === item.value;
                    const Icon = item.Icon;
                    return (
                      <Box
                        key={item.value}
                        onClick={() => { setObjectType(item.value); form.setFieldsValue({ objectType: item.value }); }}
                        sx={{
                          cursor: 'pointer', userSelect: 'none',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          gap: 0.5, py: 1.25, px: 0.5, borderRadius: '12px',
                          border: '1.5px solid',
                          borderColor: active ? '#2A66C6' : '#E5E7EB',
                          backgroundColor: active ? 'rgba(42,102,198,0.07)' : '#fff',
                          color: active ? '#2A66C6' : '#6B7280',
                          transition: 'all 0.15s ease',
                          '&:hover': { borderColor: '#2A66C6', backgroundColor: 'rgba(42,102,198,0.04)', transform: 'translateY(-1px)' }
                        }}
                      >
                        <Icon sx={{ fontSize: 20 }} />
                        <Typography sx={{ fontSize: 10.5, fontWeight: active ? 600 : 500, textAlign: 'center', lineHeight: 1.1 }}>
                          {item.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Form.Item>

            </GridItem>
          </GridContainer>

          <DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
            <Button onClick={handleClose} variant="outlined"
              sx={{ textTransform: 'none', border: '2px solid #2A66C6', color: '#2A66C6', borderRadius: '18px', width: '93px', height: '40px',
                    '&:hover': { border: '2px solid #2A66C6', backgroundColor: 'rgba(42,102,198,0.04)' } }}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={!isValid || submitting}
              startIcon={isValid ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : null}
              sx={{ textTransform: 'none', ml: 2,
                    '&.MuiButtonBase-root': { color: '#fff', backgroundColor: '#2A66C6', borderRadius: '32px', height: '40px', width: '160px', minWidth: '100px', boxShadow: '0px 1px 2px 0px rgba(16,24,40,0.05)', '&:hover': { backgroundColor: '#3C3489' } },
                    '&.Mui-disabled': { backgroundColor: '#C7D2E4', color: '#fff' } }}>
              {submitting ? 'Envoi…' : 'Confirmer'}
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
      <Snackbars severity="success" message={msg} open={success} setOpen={setSuccess} autohideduration={2000} />
      <Snackbars severity="error"   message={msg} open={error}   setOpen={setError}   autohideduration={3000} />
    </>
  );
}

export default ReassignModal;